import { track } from "@/features/analytics";
import type { IMediaFolder } from "@/shared/types";
import {
  useGetMediaFoldersQuery,
  useUpdateMediaFolderMutation,
} from "@api/media-folders";
import Button from "@components/ui/button";
import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import { ScrollArea } from "@components/ui/scroll-area";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { MediaLibDialogBase } from "../../media-lib-dialog-base";

const DIALOG_ID = "media-lib-move-folder";

interface IProps {
  folderData: IMediaFolder;
}

const getDescendantIds = (
  folderId: string,
  allFolders: IMediaFolder[],
): Set<string> => {
  const ids = new Set<string>();
  const collect = (parentId: string) => {
    for (const f of allFolders) {
      if (f.parentId === parentId) {
        ids.add(f._id);
        collect(f._id);
      }
    }
  };
  collect(folderId);
  return ids;
};

export const MediaLibFolderMoveDialog = ({ folderData }: IProps) => {
  const dispatch = useAppDispatch();

  const [updateFolder, { isLoading: isSaving }] =
    useUpdateMediaFolderMutation();
  const { data: allFolders = [], isFetching: isLoadingFolders } =
    useGetMediaFoldersQuery({ archived: false });

  const isProcessing = isLoadingFolders || isSaving;

  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    folderData.parentId ?? null,
  );
  const [isOpen, setIsOpen] = useState(false);

  const excludedIds = useMemo(() => {
    const ids = getDescendantIds(folderData._id, allFolders);
    ids.add(folderData._id);
    return ids;
  }, [folderData._id, allFolders]);

  const availableFolders = useMemo(() => {
    return allFolders.filter((f) => !excludedIds.has(f._id));
  }, [allFolders, excludedIds]);

  const topFolders = useMemo(() => {
    return availableFolders.filter((f) => !f.parentId);
  }, [availableFolders]);

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isProcessing) return;

    try {
      await updateFolder({
        id: folderData._id,
        parentId: selectedFolderId,
      }).unwrap();
    } catch (e) {
      track("error moving media folder", {
        folderId: folderData._id,
        parentId: selectedFolderId,
        error: e,
      });
      toast.error("Error moving folder");
    }

    handleClose();
  };

  const handleSelect = (id: string | null) => {
    setSelectedFolderId(id);
    setIsOpen(false);
  };

  const selectedFolder = useMemo(() => {
    if (!selectedFolderId) return null;
    return allFolders.find((f) => f._id === selectedFolderId) ?? null;
  }, [allFolders, selectedFolderId]);

  const folderTitle =
    selectedFolderId === null ? "Root" : selectedFolder?.title;

  return (
    <MediaLibDialogBase>
      <MediaLibDialogBase.Header
        title="Move to a folder"
        onClose={handleClose}
      />
      <MediaLibDialogBase.Content>
        <div>
          <span className="tw-mb-1 tw-block tw-font-sans tw-text-[13px] tw-font-semibold">
            Folder
          </span>
          <div className="tw-relative">
            {isLoadingFolders && (
              <span className="tw-absolute tw-inset-0 tw-z-1 tw-size-full tw-bg-white/50" />
            )}
            <Popover open={isOpen} onOpenChange={setIsOpen}>
              <Popover.Trigger>
                <div
                  className={cn(
                    "tw-relative tw-flex tw-h-10 tw-w-full tw-cursor-pointer tw-items-center tw-rounded-md tw-border tw-border-solid tw-border-blue-100 tw-px-4 tw-font-sans tw-text-[13px] tw-font-normal tw-text-blue",
                    isOpen &&
                      "tw-outline tw-outline-2 tw-outline-offset-2 tw-outline-[#7b17cf33]",
                  )}
                >
                  <span className={cn(!folderTitle && "tw-text-blue-500")}>
                    {folderTitle || "Select folder"}
                  </span>
                  <div className="tw-absolute tw-right-2 tw-top-1/2 tw-flex -tw-translate-y-1/2 tw-items-center">
                    {isLoadingFolders ? (
                      <Spinner />
                    ) : (
                      <Icon
                        icon="ri-arrow-down-s-fill"
                        className={cn(
                          "tw-ml-auto tw-text-xl/none tw-text-blue tw-transition-transform",
                          { "tw-rotate-180": isOpen },
                        )}
                      />
                    )}
                  </div>
                </div>
              </Popover.Trigger>
              <Popover.Content
                data-sd-is-floating-popover
                className="tw-z-[99999] tw-w-[464px] tw-rounded-lg tw-border tw-border-solid tw-border-blue-100 tw-bg-white"
              >
                <ScrollArea maxHeight={248}>
                  <div className="tw-p-2">
                    {folderData.parentId && (
                      <FolderOption
                        title="Root"
                        folderId={null}
                        selectedFolderId={selectedFolderId}
                        onSelect={handleSelect}
                        icon="ri-folders-line"
                      />
                    )}
                    {topFolders.map((f) => (
                      <FolderOption
                        key={f._id}
                        title={f.title}
                        folderId={f._id}
                        selectedFolderId={selectedFolderId}
                        onSelect={handleSelect}
                        allFolders={availableFolders}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </Popover.Content>
            </Popover>
          </div>
        </div>
      </MediaLibDialogBase.Content>
      <MediaLibDialogBase.Footer>
        <Button variant="secondary" className="tw-mr-4" onClick={handleClose}>
          Go back
        </Button>
        <Button
          disabled={selectedFolderId === (folderData.parentId ?? null)}
          className={cn(isProcessing && "tw-cursor-not-allowed")}
          onClick={handleSubmit}
        >
          {isProcessing ? (
            <>
              <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
              Moving...
            </>
          ) : (
            "Move"
          )}
        </Button>
      </MediaLibDialogBase.Footer>
    </MediaLibDialogBase>
  );
};

MediaLibFolderMoveDialog.dialogId = DIALOG_ID;

interface IFolderOptionProps {
  title: string;
  folderId: string | null;
  folderLevel?: number;
  selectedFolderId: string | null;
  onSelect: (id: string | null) => void;
  icon?: string;
  allFolders?: IMediaFolder[];
}

const FolderOption = ({
  title,
  folderId,
  folderLevel = 0,
  selectedFolderId,
  onSelect,
  icon,
  allFolders = [],
}: IFolderOptionProps) => {
  const isActive = folderId === selectedFolderId;

  const subFolders = useMemo(() => {
    if (!folderId) return [];
    return allFolders.filter((f) => f.parentId === folderId);
  }, [allFolders, folderId]);

  return (
    <div className="tw-relative">
      <div
        className={cn(
          "tw-flex tw-h-9 tw-w-full tw-cursor-pointer tw-items-center tw-rounded tw-pr-3 tw-font-sans tw-text-[11px] tw-font-normal tw-text-blue tw-transition-colors",
          isActive ? "tw-bg-primary-50 tw-text-primary" : "hover:tw-bg-blue-50",
        )}
        style={{
          paddingLeft: 12 + (folderLevel > 0 ? 12 + folderLevel * 16 : 0),
        }}
        onClick={() => onSelect(folderId)}
      >
        {folderLevel === 0 && (
          <Icon
            icon={icon || "ri-folder-line"}
            className={cn(
              "tw-mr-2 tw-text-blue-500",
              isActive && "tw-text-primary",
            )}
          />
        )}
        {title}
      </div>
      {subFolders.map((f) => (
        <FolderOption
          key={f._id}
          folderId={f._id}
          title={f.title}
          folderLevel={folderLevel + 1}
          selectedFolderId={selectedFolderId}
          onSelect={onSelect}
          allFolders={allFolders}
        />
      ))}
    </div>
  );
};
